/* Vuex module: projects, patient check-in, and clinical forms.
 * Mirrors the old Firestore-backed flow but uses PostgREST + the Express
 * /checkIn and /addForms endpoints. Live form updates use poll-subscriptions. */
import { rest, functions } from 'helpers/api'

const NS = 'patient'
const t = n => `${NS}/${n}`

export const getterTypes = {
  projects: t('projects'),
  activeProject: t('activeProject'),
  getCurrentPatient: t('getCurrentPatient'),
  forms: t('forms'),
  loading: t('loading'),
}

export const actionTypes = {
  loadProjects: t('loadProjects'),
  setActiveProject: t('setActiveProject'),
  checkInPatient: t('checkInPatient'),
  subscribePatient: t('subscribePatient'),
  subscribeForms: t('subscribeForms'),
  unsubscribe: t('unsubscribe'),
  updateForm: t('updateForm'),
  addVisit: t('addVisit'),
  completeStudy: t('completeStudy'),
  exportData: t('exportData'),
}

const M = {
  SET_PROJECTS: 'SET_PROJECTS',
  SET_ACTIVE_PROJECT: 'SET_ACTIVE_PROJECT',
  SET_PATIENT: 'SET_PATIENT',
  SET_FORMS: 'SET_FORMS',
  SET_LOADING: 'SET_LOADING',
  SET_SUB: 'SET_SUB',
}

export default {
  namespaced: true,

  state: () => ({
    projects: [],
    activeProject: null, // { id, name, active }
    patient: null, // { patient_id, project_id, initialized, study_completed }
    forms: {}, // keyed by form.key -> { key, title, subtitle, status, data, files }
    loading: false,
    subs: { patient: null, forms: null },
  }),

  getters: {
    projects: s => s.projects,
    activeProject: s => s.activeProject,
    getCurrentPatient: s => s.patient,
    forms: s => s.forms,
    loading: s => s.loading,
  },

  mutations: {
    [M.SET_PROJECTS](s, v) {
      s.projects = v
    },
    [M.SET_ACTIVE_PROJECT](s, v) {
      s.activeProject = v
    },
    [M.SET_PATIENT](s, v) {
      s.patient = v
    },
    [M.SET_FORMS](s, v) {
      s.forms = v
    },
    [M.SET_LOADING](s, v) {
      s.loading = v
    },
    [M.SET_SUB](s, { which, fn }) {
      if (s.subs[which]) s.subs[which]() // unsubscribe previous
      s.subs[which] = fn
    },
  },

  actions: {
    async loadProjects({ commit, rootGetters }) {
      const all = await rest.listProjects()
      // Admins see every project; operators are scoped to the projects an admin
      // has assigned them (operator_projects). UX-level filter only.
      const isAdmin = rootGetters['auth/isAdmin'] === true
      const user = rootGetters['auth/currentUser']
      let projects = all
      if (!isAdmin && user && user.uid) {
        try {
          const allowed = new Set(
            await rest.listProjectIdsForOperator(user.uid)
          )
          projects = all.filter(p => allowed.has(p.id))
        } catch (e) {
          // On lookup failure, fail closed (show nothing) rather than leak.
          // eslint-disable-next-line no-console
          console.error('Failed to scope projects to operator:', e)
          projects = []
        }
      }
      commit(M.SET_PROJECTS, projects)
      return projects
    },

    setActiveProject({ commit, state }, slug) {
      const proj = state.projects.find(p => p.id === slug) || {
        id: slug,
        name: slug,
        active: true,
      }
      commit(M.SET_ACTIVE_PROJECT, proj)
    },

    // Normalizes + checks in (creates if new), then loads the patient row.
    async checkInPatient({ commit }, patientId) {
      commit(M.SET_LOADING, true)
      try {
        const patient = await functions.checkIn(patientId)
        commit(M.SET_PATIENT, patient)
        if (patient && patient.project_id) {
          commit(M.SET_ACTIVE_PROJECT, {
            id: patient.project_id,
            name: patient.project_id.toUpperCase(),
            active: true,
          })
        }
        return patient
      } finally {
        commit(M.SET_LOADING, false)
      }
    },

    // Live patient document (poll).
    subscribePatient({ commit }, patientId) {
      const fn = rest.subscribe(
        () => rest.getPatient(patientId),
        row => commit(M.SET_PATIENT, row)
      )
      commit(M.SET_SUB, { which: 'patient', fn })
    },

    // Live forms collection (poll) -> keyed map by form.key.
    subscribeForms({ commit }, patientId) {
      const fn = rest.subscribe(
        () => rest.listForms(patientId),
        rows => {
          const map = {}
          rows.forEach(r => {
            map[r.key] = r
          })
          commit(M.SET_FORMS, map)
          commit(M.SET_LOADING, false)
        }
      )
      commit(M.SET_SUB, { which: 'forms', fn })
    },

    unsubscribe({ state }) {
      Object.keys(state.subs).forEach(k => {
        if (state.subs[k]) state.subs[k]()
      })
    },

    // Persist a form's answers/status. `patch` may include data, files, status.
    async updateForm(_ctx, { patientId, key, patch }) {
      return rest.updateForm(patientId, key, patch)
    },

    async addVisit({ state }, { patientId, numberOfVisit }) {
      const slug = state.activeProject
        ? state.activeProject.id
        : patientId.split('-')[1].toLowerCase()
      return functions.addForms(patientId, slug, numberOfVisit)
    },

    async completeStudy({ state, commit }, completed) {
      const patientId = state.patient.patient_id
      const rows = await rest.updatePatient(patientId, {
        study_completed: !!completed,
      })
      if (rows && rows[0]) commit(M.SET_PATIENT, rows[0])
    },

    async exportData(_ctx, projectSlug) {
      return functions.exportData(projectSlug)
    },
  },
}
